import { Component, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { CyclistService } from 'src/app/services/cyclist.service';
import { HelpersService } from 'src/app/services/helpers.service';


@Component({
  selector: 'app-workshop-jobcard-search-customer',
  templateUrl: './workshop-jobcard-search-customer.component.html',
  styleUrls: ['./workshop-jobcard-search-customer.component.scss']
})
export class WorkshopJobcardSearchCustomerComponent {

  @Input() jobcard: any;

  searchTerm: string = '';
  isloading = false;
  results: any[] = [];
  emailSearchIncomplete = false;
  phoneSearchIncomplete = false;
  private searchSubject = new Subject<string>();
  private searchResultsLength = 50;


  constructor(
    public activeModal: NgbActiveModal,
    private translate: TranslateService,
    private cyclistService: CyclistService,
    private helperService: HelpersService
  ) { }

  ngOnInit() {
    this.searchSubject.pipe(debounceTime(500))
      .subscribe(term =>
        this.searchCustomers(term)
      );

  }

  onSearchChange(event) {
    this.searchSubject.next(event);
  }

  searchCustomers(search: string) {
    this.results = [];
    if (search.length < 4) {
      return;
    }


    const searchType = this.detectSearchType(search);

    this.phoneSearchIncomplete = false;
    this.emailSearchIncomplete = false;

    if (searchType === 'phone' && !this.cyclistService.isValidPhoneNumber(search)) {
      this.phoneSearchIncomplete = true;
      this.results = [];
      return;
    }
    if (searchType === 'email' && !this.cyclistService.isValidEmail(search)) {
      this.emailSearchIncomplete = true;
      this.results = [];
      return;
    }

    this.isloading = true;
    this.cyclistService.searchCyclistsV2(this.helperService.getPartnerId(), 0, this.searchResultsLength, search, searchType).subscribe({
      next: (res: any) => {
        this.results = res.cyclists || [];
        this.isloading = false;
      },
      error: (err) => {
        console.error('Error searching customers:', err);
        this.isloading = false;
      }
    });
  }

  private detectSearchType(search: string): 'email' | 'phone' | 'name' {
    // Email regex pattern
    const hasAtSymbol = search.includes('@');

    // Phone number regex patterns (adjust based on your requirements)
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{4,}$/; // Basic phone pattern

    if (hasAtSymbol) {
      return 'email';
    } else if (phoneRegex.test(search.trim().replace(/[\s\-\(\)]/g, ''))) {
      return 'phone';
    } else {
      return 'name';
    }
  }


  createNewCustomer() {
    this.activeModal.close({ action: 'create', customer: null, search: this.searchTerm, type: this.detectSearchType(this.searchTerm) });
  }

  selectedCustomer(event, customer) {
    event.stopPropagation();
    if (customer.ID === 0) {
      //Create the customer in the PCL
      this.linkStorageAccount(customer);
      return;
    }
    this.jobcard.customer = { ...customer };
    this.activeModal.close({ ...this.jobcard });
  }

  linkStorageAccount(customer, edit = false) {
    this.cyclistService.linkCyclists(this.helperService.getPartnerId(), customer).subscribe((response) => {
      if (response && response.Data && response.Data.length > 0) {
        this.jobcard.customer = {
          ID: response.Data[0].ID,
          Name: response.Data[0].CustomerName,
          Surname: response.Data[0].CustomerSurname,
          Email: response.Data[0].CustomerEmail,
          PhoneNumber: response.Data[0].CustomerMobile,
          ExternalID: response.Data[0].ExternalID || 0
        };
        if (edit) {
          this.activeModal.close({ action: 'edit', customer: this.jobcard.customer });
        } else {
          this.activeModal.close({ ...this.jobcard });
        }
      } else {
        console.error('Error linking customer:', response);
      }
    }, err => {

    });
  }

  showOptions(event, customer) {
    event.stopPropagation();
    // Reset all other customers' showActions
    this.results.forEach(c => {
      if (c !== customer) {
        c.showActions = false;
      }
    });

    customer.showActions = !customer.showActions;

    if (customer.showActions) {
      // Check if dropdown would be cut off and set positioning
      setTimeout(() => {
        this.adjustDropdownPosition(event.target);
      }, 0);
    }
  }

  private adjustDropdownPosition(buttonElement: HTMLElement) {
    const searchResultsContainer = buttonElement.closest('.search-results');
    const dropdown = buttonElement.closest('.action-button')?.querySelector('.line-actions-dropdown') as HTMLElement;

    if (!searchResultsContainer || !dropdown) return;

    const containerRect = searchResultsContainer.getBoundingClientRect();
    const buttonRect = buttonElement.getBoundingClientRect();
    const dropdownHeight = 100; // Approximate height of dropdown

    const spaceBelow = containerRect.bottom - buttonRect.bottom;
    const spaceAbove = buttonRect.top - containerRect.top;

    // If not enough space below but enough space above, position upward
    if (spaceBelow < dropdownHeight && spaceAbove >= dropdownHeight) {
      dropdown.classList.add('dropdown-up');
    } else {
      dropdown.classList.remove('dropdown-up');
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    this.results.forEach(customer => {
      customer.showActions = false;
    });
  }

  editCustomer(event, customer) {
    event.stopPropagation();
    if (customer.ID === 0) {
      //Create the customer in the PCL
      this.linkStorageAccount(customer, true);
    } else {
      this.activeModal.close({ action: 'edit', customer: customer });
    }
  }
  removeCustomer(event, customer) {
    event.stopPropagation();
    this.activeModal.close({ action: 'delete', customer: customer });
  }

  @HostListener('document:keydown.enter', ['$event'])
  handleEnter(event: KeyboardEvent) {
    event.stopPropagation();
    setTimeout(() => {
      this.createNewCustomer();
    }, 100); // Delay to ensure the modal is ready
  }
}
